import "./AdminTagsPage.scss";
import "../Home/HomePage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { AdminContentApi } from "../../services/api/adminContent";
import { DirectionResponse, TagResponse, TagUpsertRequest } from "../../types/types";
import { isAdminRole } from "../../utils/roles";
import { getErrorMessage } from "../../utils/errorMessage";

type TagWithDirection = TagResponse & { directionId: number; directionName: string };

const EMPTY_FORM: TagUpsertRequest = { directionId: 0, name: "" };

export const AdminTagsPage = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [tags, setTags] = useState<TagWithDirection[]>([]);
  const [directions, setDirections] = useState<DirectionResponse[]>([]);
  const [createForm, setCreateForm] = useState<TagUpsertRequest>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingForm, setEditingForm] = useState<TagUpsertRequest>(EMPTY_FORM);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccess = useMemo(() => isAdminRole(user?.role), [user?.role]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedDirections = await AdminContentApi.getDirections();
      setDirections(fetchedDirections);

      const directionTags = await Promise.all(
        fetchedDirections.map(async (direction) => {
          const tags = await AdminContentApi.getDirectionTags(direction.id);
          return tags.map((tag) => ({
            ...tag,
            directionId: direction.id,
            directionName: direction.name,
          }));
        })
      );
      setTags(directionTags.flat());
    } catch (requestError) {
      const message = getErrorMessage(requestError, "Не удалось загрузить теги. Попробуйте еще раз.");
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    void fetchAll();
  }, [canAccess, fetchAll]);

  const resetCreateForm = useCallback(() => setCreateForm({ ...EMPTY_FORM, directionId: directions[0]?.id ?? 0 }), [directions]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  }, [resetCreateForm]);

  const handleCreate = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSaving(true);
      setError(null);

      try {
        const created = await AdminContentApi.createTag(createForm);
        const direction = directions.find((d) => d.id === createForm.directionId);
        setTags((current) => [
          ...current,
          { ...created, directionId: createForm.directionId, directionName: direction?.name ?? "" },
        ]);
        closeCreateModal();
      } catch (requestError) {
        const message = getErrorMessage(requestError, "Не удалось создать тег.");
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [closeCreateModal, createForm, directions]
  );

  const startEdit = useCallback(
    (item: TagWithDirection) => {
      setEditingId(item.id);
      setEditingForm({ directionId: item.directionId, name: item.name });
      setError(null);
    },
    []
  );

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingForm(EMPTY_FORM);
  }, []);

  const handleUpdate = useCallback(
    async (id: number) => {
      setSaving(true);
      setError(null);

      try {
        const updated = await AdminContentApi.updateTag(id, editingForm);
        const direction = directions.find((d) => d.id === editingForm.directionId);
        setTags((current) =>
          current.map((item) =>
            item.id === id
              ? { ...updated, directionId: editingForm.directionId, directionName: direction?.name ?? item.directionName }
              : item
          )
        );
        cancelEdit();
      } catch (requestError) {
        const message = getErrorMessage(requestError, "Не удалось обновить тег.");
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editingForm, directions]
  );

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот тег?")) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await AdminContentApi.deleteTag(id);
      setTags((current) => current.filter((item) => item.id !== id));
    } catch (requestError) {
      const message = getErrorMessage(requestError, "Не удалось удалить тег.");
      setError(message);
    } finally {
      setSaving(false);
    }
  }, []);

  if (!canAccess) {
    return (
      <>
        <Header />
        <main className="admin-tags">
          <section className="admin-tags__card">
            <p className="admin-tags__empty">Доступ к этому разделу есть только у администратора.</p>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="admin-tags">
        <section className="admin-tags__hero">
          <div>
            <button className="admin-tags__back" type="button" onClick={() => navigate("/app/admin")}>
              ← Назад
            </button>
            <p className="admin-tags__eyebrow">Админка</p>
            <h1 className="admin-tags__title">Теги</h1>
            <p className="admin-tags__subtitle">
              Управление тегами вопросов: создание, редактирование и удаление.
            </p>
          </div>
          <aside className="admin-tags__summary">
            <div>
              <strong>Всего:</strong> {tags.length}
            </div>
          </aside>
        </section>

        <section className="admin-tags__card">
          <div className="admin-tags__toolbar">
            <button
              className="admin-tags__button admin-tags__buttonPrimary"
              type="button"
              onClick={() => {
                resetCreateForm();
                setIsCreateModalOpen(true);
              }}
            >
              Добавить тег
            </button>
          </div>
          {error ? <p className="admin-tags__error">{error}</p> : null}
        </section>

        <section className="admin-tags__card admin-tags__card_table">
          <div className="admin-tags__tableWrap">
            <table className="admin-tags__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Направление</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tags.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <p className="admin-tags__empty">{loading ? "Загрузка..." : "Теги не найдены."}</p>
                    </td>
                  </tr>
                ) : (
                  tags.map((item) => {
                    const isEditing = editingId === item.id;
                    return (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-tags__input"
                              value={editingForm.name}
                              onChange={(event) => setEditingForm((current) => ({ ...current, name: event.target.value }))}
                            />
                          ) : item.name}
                        </td>
                        <td>
                          {isEditing ? (
                            <select
                              className="admin-tags__input"
                              value={editingForm.directionId}
                              onChange={(event) =>
                                setEditingForm((current) => ({ ...current, directionId: Number(event.target.value) }))
                              }
                            >
                              {directions.map((direction) => (
                                <option key={direction.id} value={direction.id}>
                                  {direction.name}
                                </option>
                              ))}
                            </select>
                          ) : item.directionName}
                        </td>
                        <td>
                          <div className="admin-tags__actions">
                            {isEditing ? (
                              <>
                                <button
                                  className="admin-tags__button admin-tags__buttonPrimary"
                                  type="button"
                                  onClick={() => void handleUpdate(item.id)}
                                  disabled={saving}
                                >
                                  Сохранить
                                </button>
                                <button className="admin-tags__button" type="button" onClick={cancelEdit} disabled={saving}>
                                  Отмена
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="admin-tags__button" type="button" onClick={() => startEdit(item)} disabled={saving}>
                                  Изменить
                                </button>
                                <button
                                  className="admin-tags__button admin-tags__buttonDanger"
                                  type="button"
                                  onClick={() => void handleDelete(item.id)}
                                  disabled={saving}
                                >
                                  Удалить
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {isCreateModalOpen ? (
        <section className="home-modal">
          <div className="home-modal__backdrop" onClick={closeCreateModal} />
          <div className="home-modal__dialog">
            <button className="home-modal__close" type="button" onClick={closeCreateModal}>x</button>
            <h2 className="home-modal__title">Новый тег</h2>
            <form className="home-modal__form" onSubmit={handleCreate}>
              <select
                className="home-modal__input"
                value={createForm.directionId}
                onChange={(event) => setCreateForm((current) => ({ ...current, directionId: Number(event.target.value) }))}
              >
                <option value={0} disabled>Выберите направление</option>
                {directions.map((direction) => (
                  <option key={direction.id} value={direction.id}>
                    {direction.name}
                  </option>
                ))}
              </select>
              <input
                className="home-modal__input"
                type="text"
                placeholder="Название тега"
                value={createForm.name}
                onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
              />
              <button className="home-modal__submit" type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Создать"}
              </button>
            </form>
          </div>
        </section>
      ) : null}
    </>
  );
};
